import React from 'react';
import { action } from 'mobx';
import baseStyles from './os_input.module.css';
import messages from './os_input_messages';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { OSInputStore } from './os_input_store';
import { SearchInput } from '../search_input/search_input';
import { getOSString, osList } from './os_input_data';
import { ButtonClickEvent } from 'ui/base/types';
import { observer } from 'mobx-react';

type OSInputStyles = {
  inputButton: string;
  osItemContainer: string;
  nameContainer: string;
  placeholder: string;
  osContainer: string;
  osText: string;
  removeIcon: string;
};

const themedStyles: ThemedStyles<OSInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    osItemContainer: baseStyles.osItemContainer,
    nameContainer: baseStyles.nameContainer,
    placeholder: baseStyles.daylightPlaceholder,
    osContainer: baseStyles.osContainer,
    osText: baseStyles.osText,
    removeIcon: baseStyles.daylightRemoveIcon,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    osItemContainer: baseStyles.osItemContainer,
    nameContainer: baseStyles.nameContainer,
    placeholder: baseStyles.midnightPlaceholder,
    osContainer: baseStyles.osContainer,
    osText: baseStyles.osText,
    removeIcon: baseStyles.midnightRemoveIcon,
  },
};

type TypeInputDisplayProps = {
  store: OSInputStore;
};

const TypeInputDisplay = observer(({ store }: TypeInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.modal.showModal();
  }, [store]);

  const onRemoveType = React.useCallback(action((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    store.removeType();
  }), [store]);

  if (store.os !== undefined) {
    return (
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.osContainer}>
          <div className={styles.osText}>
            {getOSString(store.os)}
          </div>
          <div onClick={onRemoveType}>
            <div className={styles.removeIcon}/>
          </div>
        </div>
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <span className={styles.placeholder}>{messages.placeholder()}</span>
      </button>
    );
  }
});

type OSData = {
  cpe: string;
  name: string;
};

type OSItemProps = {
  data: OSData;
  store: OSInputStore;
  key: string;
};

const OSItem = ({ data, store }: OSItemProps) => {
  const styles = useStyles(themedStyles);
  const onClick = React.useCallback(action(() => {
    store.commitType(data.cpe);
    store.search.modal.hideModal();
  }), [store, data]);
  return (
    <div className={styles.osItemContainer} onClick={onClick}>
      <div className={styles.nameContainer}>
        {data.name}
      </div>
    </div>
  );
};

const filterTypes = (item: OSData, text: string) => {
  if (text) {
    if (item.name) {
      return item.name.toLowerCase().indexOf(text) >= 0;
    }
    return false;
  }
  return true;
};

type OSInputProps = {
  store: OSInputStore;
};

export const OSInput = observer(({ store }: OSInputProps) => {
  const Display = React.useCallback(() => (
    <TypeInputDisplay store={store}/>
  ), [store]);
  const ItemComponent = (props: Omit<OSItemProps, 'store'>) => {
    return <OSItem store={store} {...props}/>;
  };
  return (
    <SearchInput
      placeholder={messages.search()}
      store={store.search}
      data={osList}
      filterFunc={filterTypes}
      keyFunc={item => item.name}
      ItemComponent={ItemComponent}
      DisplayComponent={Display}
    />
  );
});